/** Marketing i18n fragment — intgSlack (integrations/slack). */

export const intgSlackEn = {
  meta: {
    title: "Slack integration — ticket questions, approvals and governed intake",
    description:
      "Ask about existing tickets, approve plans and patches from a decision card, and route new work into FixControl, all from the Slack thread where it came up. Each capability reports its own health, and outbound posts go through a retried, audited outbox.",
  },
  hero: {
    eyebrow: "Slack integration",
    title: "Ticket questions and approvals",
    highlight: "inside Slack",
    subtitle:
      "Ask what’s open across FixControl, Jira, Freshdesk, Odoo, and TOPdesk. Approve plans, patches, replies, and deployments from a decision card in the thread. Every decision is tied to a linked identity and recorded in the decision ledger.",
    primaryCta: "Book a demo",
    secondaryCta: "See ticket questions",
    bullet1: "Decision cards in-thread",
    bullet2: "Linked, verified identities",
    bullet3: "Per-capability health",
  },
  what: {
    eyebrow: "What it does",
    title: "Answer questions, collect approvals, route new work",
  },
  features: {
    intake: {
      title: "Governed intake",
      body: "Mention the bot and the message becomes a work item in the bound project, inside your own organization. Plain language works after an answer too: “create a ticket for this” opens one behind a confirmation card, and “send it to the customer” stages the answer as a governed customer reply awaiting approval.",
    },
    questions: {
      title: "Ticket questions",
      body: "Ask what’s open or waiting on approval across FixControl, Jira, Freshdesk, Odoo, and TOPdesk. An intent gate runs before intake, so a question stays a question instead of turning into a new ticket.",
    },
    approvals: {
      title: "Approvals on decision cards",
      body: "Plans, patches, replies, and deployment gates arrive as decision cards in the channel. Each approve or request-changes decision is recorded with your identity, role, and channel.",
    },
    sync: {
      title: "Cards stay in sync",
      body: "When someone decides in Slack, the card updates and the operational timeline records it. The app and the thread show the same state.",
    },
    identities: {
      title: "Verified identities",
      body: "A Slack user links their FixControl identity before they can decide anything. Identities carry verified and elevated tiers, and policy can require the elevated tier for higher-risk approvals.",
    },
    replies: {
      title: "Channel-aware replies",
      body: "Replies post back into the thread that asked. In public or shared channels, answers are redacted to match channel visibility, so internal ticket detail stays inside your tenant.",
    },
  },
  capabilities: {
    eyebrow: "Capabilities & health",
    title: "Each Slack capability reports its own status",
    description:
      "Intake can be healthy while posting fails — say, when the bot was never invited to a channel. FixControl tracks health per capability and shows it, so you know which paths work before you rely on them.",
    intake: {
      name: "Receive messages & mentions (intake)",
      body: "Messages routed to the bot become governed work items in the bound project, inside your own organization. Ticket creation from plain language is confirmed before it runs, and a requested customer reply becomes an approval card in the channel — deciding it requires a linked, elevated Slack identity.",
    },
    answering: {
      name: "Ticket-question answering",
      body: "Answers questions about existing FixControl, Jira, Freshdesk, Odoo, and TOPdesk tickets, with a pre-intake gate that keeps a question from creating an issue.",
    },
    cards: {
      name: "Approval decision cards",
      body: "Posts cards for plans, patches, replies, and deployment gates, and records each approve or request-changes decision with attribution.",
    },
    posting: {
      name: "Post replies & card updates",
      body: "Requires the workspace to be linked and the bot present in the channel. When those are missing, the posting capability reports unhealthy while intake keeps working.",
    },
    binding: {
      name: "Verified / elevated identity binding",
      body: "Available once a Slack user links their FixControl identity. Governance policy can require the elevated tier before someone approves higher-risk actions.",
    },
    singleStatus: {
      name: "A single green ‘connected’ badge",
      body: "FixControl doesn’t show one. A workspace can be fine for intake and broken for posting, so status is reported per capability.",
    },
  },
  failures: {
    eyebrow: "When a post fails",
    title: "A failed Slack post is a visible event",
    noteTitle: "Delivery through the integration outbox",
    noteBody:
      "Every outbound Slack write — a reply, a card update, an acknowledgement — is queued in the integration outbox. Transient failures retry; persistent ones dead-letter and appear on the operational timeline. Approvals taken in Slack are written to the decision ledger with actor, role, channel, evidence, and consequence.",
  },
  faq: {
    eyebrow: "FAQ",
    title: "Questions teams ask about the Slack app",
    q1: {
      q: "Is Slack just a chatbot in FixControl?",
      a: "No. Chat is the interface; governance runs underneath it. Ticket answers, intake, and approvals are each scoped to a project, checked against permissions and capability health, and recorded.",
    },
    q2: {
      q: "What happens if a Slack post fails?",
      a: "It waits in the outbox and retries. If it keeps failing, it’s dead-lettered and flagged, so an undelivered reply shows up as a failure on the timeline.",
    },
    q3: {
      q: "Who can approve from Slack?",
      a: "Only Slack users whose linked FixControl identity carries the required role. Higher-risk actions can be limited to the elevated identity tier, and each decision records who took it and from which channel.",
    },
    q4: {
      q: "Are approvals from Slack audited?",
      a: "Yes. A decision made on a Slack card lands in the same decision ledger as an in-app approval, with the same fields recorded.",
    },
    q5: {
      q: "Can sensitive ticket detail leak into a public channel?",
      a: "Answers respect tenant isolation and the visibility of the channel they post into. In public or shared channels, internal ticket detail is redacted from the response.",
    },
  },
  related: {
    ticketQuestions: {
      label: "Ticket questions",
      description: "Get answers about open work without filing another ticket.",
    },
    ledger: {
      label: "Approval ledger",
      description: "How Slack decisions land in the audit trail.",
    },
    teams: {
      label: "Microsoft Teams",
      description: "Approvals and intake for organizations on Microsoft Teams.",
    },
  },
  cta: {
    title: "See a Slack approval end to end",
    description:
      "Book a demo and watch a question, an approval, and the matching ledger entry happen in a live workspace.",
    primary: "Book a demo",
    secondary: "Ask FixControl",
  },
};

export const intgSlackNl = {
  meta: {
    title: "Slack-integratie — ticketvragen, goedkeuringen en gegoverneerde intake",
    description:
      "Stel vragen over bestaande tickets, keur plannen en patches goed vanaf een beslis-kaart en route nieuw werk naar FixControl — allemaal vanuit de Slack-thread waar het opkwam. Elke capability rapporteert zijn eigen health en uitgaande posts lopen via een herprobeerde, geauditeerde outbox.",
  },
  hero: {
    eyebrow: "Slack-integratie",
    title: "Ticketvragen en goedkeuringen",
    highlight: "in Slack",
    subtitle:
      "Vraag wat er openstaat in FixControl, Jira, Freshdesk, Odoo en TOPdesk. Keur plannen, patches, antwoorden en deployments goed vanaf een beslis-kaart in de thread. Elke beslissing hangt aan een gekoppelde identiteit en wordt vastgelegd in het beslis-grootboek.",
    primaryCta: "Boek een demo",
    secondaryCta: "Bekijk ticketvragen",
    bullet1: "Beslis-kaarten in de thread",
    bullet2: "Gekoppelde, geverifieerde identiteiten",
    bullet3: "Health per capability",
  },
  what: {
    eyebrow: "Wat het doet",
    title: "Vragen beantwoorden, goedkeuringen verzamelen en nieuw werk routeren",
  },
  features: {
    intake: {
      title: "Gegoverneerde intake",
      body: "Noem de bot en het bericht wordt een werkitem in het gekoppelde project, binnen je eigen organisatie. Gewone taal werkt ook na een antwoord: “maak hier een ticket van” opent er een achter een bevestigingskaart, en “stuur dit naar de klant” zet het antwoord klaar als gegoverneerd antwoord aan die klant, dat op goedkeuring wacht.",
    },
    questions: {
      title: "Ticketvragen",
      body: "Vraag wat er openstaat of op goedkeuring wacht in FixControl, Jira, Freshdesk, Odoo en TOPdesk. Een intent-gate draait vóór intake, zodat een vraag een vraag blijft in plaats van een nieuw ticket te worden.",
    },
    approvals: {
      title: "Goedkeuringen op beslis-kaarten",
      body: "Plannen, patches, antwoorden en deployment-poorten komen als beslis-kaarten in het kanaal binnen. Elke approve- of request-changes-beslissing wordt vastgelegd met je identiteit, rol en kanaal.",
    },
    sync: {
      title: "Kaarten blijven in sync",
      body: "Beslist iemand in Slack, dan werkt de kaart bij en legt de operationele tijdlijn het vast. De app en de thread tonen dezelfde staat.",
    },
    identities: {
      title: "Geverifieerde identiteiten",
      body: "Een Slack-gebruiker koppelt zijn FixControl-identiteit voordat hij iets kan beslissen. Identiteiten kennen een geverifieerd en een verhoogd niveau, en beleid kan het verhoogde niveau eisen voor goedkeuringen met hoger risico.",
    },
    replies: {
      title: "Kanaal-bewuste antwoorden",
      body: "Antwoorden worden teruggeplaatst in de thread die de vraag stelde. In publieke of gedeelde kanalen worden antwoorden geredigeerd naar de zichtbaarheid van het kanaal, zodat interne ticketdetails binnen je tenant blijven.",
    },
  },
  capabilities: {
    eyebrow: "Capabilities & health",
    title: "Elke Slack-capability rapporteert zijn eigen status",
    description:
      "Intake kan gezond zijn terwijl posten faalt — bijvoorbeeld als de bot nooit in een kanaal is uitgenodigd. FixControl houdt de health per capability bij en toont die, zodat je weet welke paden werken voordat je erop vertrouwt.",
    intake: {
      name: "Berichten & mentions ontvangen (intake)",
      body: "Berichten die naar de bot worden gerouteerd, worden gegoverneerde werkitems in het gekoppelde project, binnen je eigen organisatie. Een ticket aanmaken vanuit gewone taal wordt eerst bevestigd, en een gevraagd klantantwoord wordt een goedkeuringskaart in het kanaal — beslissen vereist een gekoppelde, verhoogde Slack-identiteit.",
    },
    answering: {
      name: "Beantwoorden van ticketvragen",
      body: "Beantwoordt vragen over bestaande FixControl-, Jira-, Freshdesk-, Odoo- en TOPdesk-tickets, met een pre-intake-gate die voorkomt dat een vraag een issue aanmaakt.",
    },
    cards: {
      name: "Beslis-kaarten voor goedkeuring",
      body: "Plaatst kaarten voor plannen, patches, antwoorden en deployment-poorten, en legt elke approve- of request-changes-beslissing vast met attributie.",
    },
    posting: {
      name: "Antwoorden & kaart-updates plaatsen",
      body: "Vereist dat de workspace gekoppeld is en de bot in het kanaal aanwezig is. Ontbreekt dat, dan rapporteert de post-capability ongezond terwijl intake blijft werken.",
    },
    binding: {
      name: "Geverifieerde / verhoogde identiteitskoppeling",
      body: "Beschikbaar zodra een Slack-gebruiker zijn FixControl-identiteit koppelt. Governance-beleid kan het verhoogde niveau eisen voordat iemand acties met hoger risico goedkeurt.",
    },
    singleStatus: {
      name: "Eén groene ‘connected’-badge",
      body: "Die toont FixControl niet. Een workspace kan prima werken voor intake en stuk zijn voor posten, dus status wordt per capability gerapporteerd.",
    },
  },
  failures: {
    eyebrow: "Als een post faalt",
    title: "Een mislukte Slack-post is een zichtbare gebeurtenis",
    noteTitle: "Levering via de integratie-outbox",
    noteBody:
      "Elke uitgaande Slack-schrijfactie — een antwoord, een kaart-update, een bevestiging — wordt in de integratie-outbox gezet. Tijdelijke fouten worden opnieuw geprobeerd; hardnekkige belanden in de dead-letter en verschijnen op de operationele tijdlijn. Goedkeuringen genomen in Slack worden naar het beslis-grootboek geschreven met actor, rol, kanaal, bewijs en consequentie.",
  },
  faq: {
    eyebrow: "FAQ",
    title: "Wat teams vragen over de Slack-app",
    q1: {
      q: "Is Slack alleen een chatbot in FixControl?",
      a: "Nee. Chat is de interface; de governance draait eronder. Ticketantwoorden, intake en goedkeuringen zijn elk gescoped op een project, getoetst aan permissies en capability-health, en vastgelegd.",
    },
    q2: {
      q: "Wat gebeurt er als een Slack-post mislukt?",
      a: "Hij wacht in de outbox en wordt opnieuw geprobeerd. Blijft het mislukken, dan gaat hij naar de dead-letter en wordt gemarkeerd, zodat een niet-afgeleverd antwoord als fout op de tijdlijn verschijnt.",
    },
    q3: {
      q: "Wie mag vanuit Slack goedkeuren?",
      a: "Alleen Slack-gebruikers van wie de gekoppelde FixControl-identiteit de vereiste rol draagt. Acties met hoger risico kunnen worden beperkt tot het verhoogde identiteitsniveau, en elke beslissing legt vast wie hem nam en vanuit welk kanaal.",
    },
    q4: {
      q: "Worden goedkeuringen vanuit Slack geaudit?",
      a: "Ja. Een beslissing op een Slack-kaart belandt in hetzelfde beslis-grootboek als een goedkeuring in-app, met dezelfde velden vastgelegd.",
    },
    q5: {
      q: "Kan gevoelige ticketinformatie in een publiek kanaal lekken?",
      a: "Antwoorden respecteren tenantisolatie en de zichtbaarheid van het kanaal waarin ze verschijnen. In publieke of gedeelde kanalen worden interne ticketdetails uit het antwoord geredigeerd.",
    },
  },
  related: {
    ticketQuestions: {
      label: "Ticketvragen",
      description: "Krijg antwoord over openstaand werk zonder nog een ticket aan te maken.",
    },
    ledger: {
      label: "Goedkeurings-grootboek",
      description: "Hoe Slack-beslissingen in de audittrail belanden.",
    },
    teams: {
      label: "Microsoft Teams",
      description: "Goedkeuringen en intake voor organisaties op Microsoft Teams.",
    },
  },
  cta: {
    title: "Zie een Slack-goedkeuring van begin tot eind",
    description:
      "Boek een demo en zie een vraag, een goedkeuring en de bijbehorende grootboekregel gebeuren in een live workspace.",
    primary: "Boek een demo",
    secondary: "Vraag het FixControl",
  },
};
